import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  trackAnalyticsEvent,
  trackPageView,
} from "../services/firebase.service";

const SCROLL_MILESTONES = [25, 50, 75, 100];
const PAGE_VIEW_DEDUPE_WINDOW_MS = 1200;

let lastTrackedPage = "";
let lastTrackedAt = 0;

export default function AppAnalytics() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return;
    }

    // The buy page owns its six funnel events, including its visit event.
    if (location.pathname === "/buy" || location.pathname === "/buy/") {
      return;
    }

    const pagePath = `${location.pathname}${location.search}${location.hash}`;
    const now = Date.now();
    const recentlyTrackedSamePage =
      lastTrackedPage === pagePath &&
      now - lastTrackedAt < PAGE_VIEW_DEDUPE_WINDOW_MS;

    if (!recentlyTrackedSamePage) {
      lastTrackedPage = pagePath;
      lastTrackedAt = now;
      void trackPageView(pagePath);
    }

    const pageStartTime = Date.now();
    const reachedMilestones = new Set<number>();

    const handleScroll = () => {
      const viewportHeight = window.innerHeight;
      const fullHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY;
      const maxScrollable = fullHeight - viewportHeight;
      const percentScrolled =
        maxScrollable <= 0
          ? 100
          : Math.round((scrollTop / maxScrollable) * 100);

      SCROLL_MILESTONES.forEach((milestone) => {
        if (percentScrolled >= milestone && !reachedMilestones.has(milestone)) {
          reachedMilestones.add(milestone);
          void trackAnalyticsEvent("scroll_depth", {
            page_path: pagePath,
            percent_scrolled: milestone,
          });
        }
      });
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);

      const engagementSeconds = Math.max(
        1,
        Math.round((Date.now() - pageStartTime) / 1000),
      );

      void trackAnalyticsEvent("page_engagement", {
        page_path: pagePath,
        engagement_time_sec: engagementSeconds,
      });
    };
  }, [location.hash, location.pathname, location.search]);

  return null;
}
